import { useCallback, useEffect, useState } from "react";
import {
  getLoginHistory,
  listMySessions,
  logoutOtherSessions,
  revokeMySession,
} from "../../api/authSessionsApi";
import { resetCsrfToken } from "../../api/apiClient";
import StatusBadge from "../midpoint/intelligence/StatusBadge";
import { formatDateTime } from "../midpoint/admin/users/adminUserFormatters";

function providerLabel(provider) {
  if (provider === "google") {
    return "Google";
  }
  if (provider === "local") {
    return "Email & password";
  }

  return provider || "Unknown";
}

const AccountSessionsPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [busySessionId, setBusySessionId] = useState("");
  const [busyOthers, setBusyOthers] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const [sessionsBody, historyBody] = await Promise.all([
        listMySessions(),
        getLoginHistory({ limit: 20 }),
      ]);
      setSessions(sessionsBody.sessions || []);
      setEvents(historyBody.events || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load security settings.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRevokeSession = async (session) => {
    if (
      !window.confirm(
        session.isCurrent
          ? "Revoking this session will sign you out. Continue?"
          : "Revoke this session?"
      )
    ) {
      return;
    }

    try {
      setBusySessionId(session.sessionId);
      setErrorMessage("");
      const body = await revokeMySession(session.sessionId);
      if (body?.requiresLogin) {
        resetCsrfToken();
        window.location.href = "/login";
        return;
      }

      setSuccessMessage("Session revoked.");
      await loadData();
    } catch (error) {
      setErrorMessage(error.message || "Unable to revoke session.");
    } finally {
      setBusySessionId("");
    }
  };

  const handleLogoutOthers = async () => {
    if (!window.confirm("Sign out all other devices and browsers?")) {
      return;
    }

    try {
      setBusyOthers(true);
      setErrorMessage("");
      const body = await logoutOtherSessions();
      setSuccessMessage(
        body?.revokedCount
          ? `Signed out ${body.revokedCount} other session(s).`
          : "No other active sessions were found."
      );
      await loadData();
    } catch (error) {
      setErrorMessage(error.message || "Unable to sign out other sessions.");
    } finally {
      setBusyOthers(false);
    }
  };

  return (
    <>
      {successMessage ? (
        <section className="panel-card account-security-flash">
          <p>{successMessage}</p>
          <button type="button" className="midpoint-share-button" onClick={() => setSuccessMessage("")}>
            Dismiss
          </button>
        </section>
      ) : null}

      {errorMessage ? (
        <section className="panel-card">
          <p className="admin-data-health-error">{errorMessage}</p>
        </section>
      ) : null}

      <section className="panel-card account-security-section">
        <div className="account-security-section__header">
          <h2>Active sessions</h2>
          <button
            type="button"
            className="midpoint-share-button"
            onClick={handleLogoutOthers}
            disabled={busyOthers || isLoading}
          >
            {busyOthers ? "Signing out…" : "Log out all other sessions"}
          </button>
        </div>

        {isLoading ? <p className="muted">Loading sessions…</p> : null}

        {!isLoading && sessions.length ? (
          <div className="admin-data-health-table-wrap">
            <table className="admin-data-health-table account-security-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Provider</th>
                  <th>IP</th>
                  <th>Created</th>
                  <th>Last seen</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.sessionId}>
                    <td>
                      {session.deviceLabel || "Unknown device"}
                      {session.isCurrent ? (
                        <StatusBadge label="Current" tone="confirmed" />
                      ) : null}
                    </td>
                    <td>{providerLabel(session.loginProvider)}</td>
                    <td>{session.ipAddress || "—"}</td>
                    <td>{formatDateTime(session.createdAt)}</td>
                    <td>{formatDateTime(session.lastSeenAt || session.createdAt)}</td>
                    <td>{formatDateTime(session.expiresAt)}</td>
                    <td>
                      {session.isActive ? (
                        <StatusBadge label="Active" tone="confirmed" />
                      ) : session.isRevoked ? (
                        <StatusBadge label="Revoked" tone="disputed" />
                      ) : (
                        <StatusBadge label="Expired" tone="neutral" />
                      )}
                    </td>
                    <td>
                      {session.isActive ? (
                        <button
                          type="button"
                          className="admin-re-inline-link"
                          disabled={busySessionId === session.sessionId}
                          onClick={() => handleRevokeSession(session)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!isLoading && !sessions.length ? <p className="muted">No sessions found.</p> : null}
      </section>

      <section className="panel-card account-security-section">
        <h2>Login history</h2>
        {isLoading ? <p className="muted">Loading history…</p> : null}

        {!isLoading && events.length ? (
          <ul className="account-security-history">
            {events.map((event) => (
              <li key={`${event.action}-${event.createdAt}`}>
                <div className="account-security-history__primary">
                  <strong>{event.label || event.action}</strong>
                  <span className="muted">{formatDateTime(event.createdAt)}</span>
                </div>
                <p className="muted account-security-history__meta">
                  {providerLabel(event.metadata?.provider)}
                  {event.ipAddress ? ` · ${event.ipAddress}` : ""}
                  {event.deviceLabel ? ` · ${event.deviceLabel}` : ""}
                  {event.outcome ? ` · ${event.outcome}` : ""}
                </p>
              </li>
            ))}
          </ul>
        ) : null}

        {!isLoading && !events.length ? <p className="muted">No recent sign-in activity.</p> : null}
      </section>
    </>
  );
};

export default AccountSessionsPanel;
